import { useState, useEffect } from 'react';
import { MdClose, MdStar, MdLocationOn, MdVerified, MdEmail } from 'react-icons/md';
import { Link } from 'react-router-dom';
import api from '../../services/api';

const getAvatarUrl = (name) => {
  const initials = name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  return `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&size=128&background=9333ea&color=fff&bold=true`;
};

export default function ProfileDialog({ userId, onClose }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const res = await api.get(`/users/${userId}`).catch(() => ({ data: null }));
        if (!cancelled && res?.data) setUser(res.data);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [userId]);

  if (!userId) return null;

  const fullName = user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() : '';
  const avatarUrl = user?.photoUrl || user?.photo_url || (fullName ? getAvatarUrl(fullName) : null);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 flex justify-end p-3 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 z-10">
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400"
            aria-label="Zatvori"
          >
            <MdClose className="text-xl" />
          </button>
        </div>
        <div className="p-6">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-10 w-10 border-2 border-primary-500 border-t-transparent" />
            </div>
          ) : user ? (
            <>
              <div className="flex flex-col items-center mb-6">
                <div className="relative">
                  <img
                    src={avatarUrl}
                    alt={fullName}
                    className="w-24 h-24 rounded-full object-cover border-4 border-primary-200 dark:border-primary-800"
                  />
                  {user.isVerified && (
                    <div className="absolute bottom-0 right-0 bg-primary-600 rounded-full p-1">
                      <MdVerified className="text-white text-sm" />
                    </div>
                  )}
                </div>
                <h2 className="mt-3 text-xl font-bold text-gray-900 dark:text-gray-100">
                  {fullName || 'Korisnik'}
                </h2>
                {user.address?.city && (
                  <p className="flex items-center gap-1 text-sm text-gray-600 dark:text-gray-400 mt-1">
                    <MdLocationOn className="text-base" />
                    {user.address.city}
                  </p>
                )}
              </div>
              <div className="space-y-3">
                {user.email && (
                  <div className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 dark:bg-gray-700/50">
                    <MdEmail className="text-gray-500 dark:text-gray-400 flex-shrink-0" />
                    <span className="text-sm text-gray-700 dark:text-gray-300 truncate">{user.email}</span>
                  </div>
                )}
                {user.rating?.average != null && (
                  <div className="flex items-center gap-2 text-sm">
                    <MdStar className="text-yellow-500 fill-yellow-500" />
                    <span>{user.rating.average?.toFixed(1) || '0.0'} ({user.rating.count || 0} ocena)</span>
                  </div>
                )}
                {user.hourlyRate && (
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Cena: {Number(user.hourlyRate).toLocaleString()} RSD/h
                  </p>
                )}
              </div>
              <Link
                to={`/babysitter/${user.id}`}
                className="mt-6 block w-full text-center py-3 rounded-xl bg-primary-600 hover:bg-primary-700 text-white font-medium"
              >
                Pogledaj pun profil
              </Link>
            </>
          ) : (
            <p className="text-center py-8 text-gray-500 dark:text-gray-400">Profil nije pronađen.</p>
          )}
        </div>
      </div>
    </div>
  );
}
